import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { IMAGE_VERSION } from '../../version.js';
import themeManager from '../../utils/themeManager';

gsap.registerPlugin(ScrollTrigger);

const Footer = () => {
  const { t } = useTranslation('home');
  const footerRef = useRef(null);
  const [theme, setTheme] = useState(themeManager.getCurrentTheme());
  const [isUserPrefDark, setIsUserPrefDark] = useState(() => typeof window !== 'undefined' ? localStorage.getItem('theme-preference') === 'dark' : false);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const ctx = gsap.context(() => {
      const footerEl = footerRef.current;
      const infoGrid = footerEl?.querySelector('.footer-info-grid');
      const infoItems = footerEl?.querySelectorAll('.footer-info-item');

      if (infoItems?.length > 0 && infoGrid) {
        gsap.from(infoItems, {
          opacity: 0,
          y: 20,
          duration: 1,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: infoGrid,
            start: "top 85%",
          }
        });
      }

      const hugeTitleEl = footerEl?.querySelector('.huge-title');
      const hugeTitleSpan = hugeTitleEl?.querySelector('span');
      if (hugeTitleEl && hugeTitleSpan) {
        gsap.from(hugeTitleSpan, {
          y: "100%",
          duration: 1.5,
          ease: "expo.out",
          scrollTrigger: {
            trigger: hugeTitleEl,
            start: "top 95%",
          }
        });
      }
    }, footerRef);

    return () => ctx.revert();
  }, []);

  // Listen for theme changes from themeManager
  useEffect(() => {
    const handleThemeChange = (event) => {
      setTheme(event.detail.theme);
      setIsUserPrefDark(typeof window !== 'undefined' ? localStorage.getItem('theme-preference') === 'dark' : false);
    };

    window.addEventListener('themeChanged', handleThemeChange);
    return () => window.removeEventListener('themeChanged', handleThemeChange);
  }, []);

  const useDark = theme === 'dark' && isUserPrefDark;

  return (
    <footer ref={footerRef} className={`pt-24 pb-8 px-6 md:px-12 lg:px-20 font-sans border-t selection:bg-black selection:text-white ${useDark ? 'bg-[#0F1115] text-white border-gray-800 selection:bg-white selection:text-black' : 'bg-[#F3F4F6] text-black border-gray-100'}`}>
      
      <div className="footer-info-grid grid grid-cols-1 lg:grid-cols-12 gap-y-16 lg:gap-x-12 items-start mb-32">
        
        {/* LEVA STRANA: Umesto ogromnog loga, suptilniji brending i poziv na kontakt */}
        <div className="lg:col-span-5 footer-info-item space-y-8">
          <div className="flex items-center gap-4">
            <img 
              src={useDark ? `/assets/images/hero-logo-dark.png?ver=${IMAGE_VERSION}` : `/assets/images/logo2.png?ver=${IMAGE_VERSION}`} 
              alt="PC Centar" 
              className="h-16 w-auto object-contain"
            />
            <div className={`h-10 w-[1px] ${useDark ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
            <p className={`text-[10px] uppercase tracking-[0.3em] font-bold leading-tight ${useDark ? 'text-gray-300' : 'text-gray-400'}`}>
              Computerland<br/>Group Member
            </p>
          </div>
          
          <h3 className={`text-3xl md:text-4xl font-bold tracking-tighter uppercase leading-none max-w-xs ${useDark ? 'text-white' : ''}`}>
            {t('footer.partnerTitle', 'VAŠ PARTNER ZA IT REŠENJA.')}
          </h3>
          
          <a href="mailto:office@pccentar.rs" className={`inline-block border-b-2 pb-1 text-xs font-bold uppercase tracking-widest transition-all px-1 ${useDark ? 'border-white hover:bg-white hover:text-black' : 'border-black hover:bg-black hover:text-white'}`}>
            {t('footer.sendInquiry', 'Pošaljite nam upit')}
          </a>
        </div>

        {/* DESNA STRANA: Information Blocks (Tvoj usklađeni raspored) */}
        <div className="lg:col-span-7 grid grid-cols-2 gap-y-12 gap-x-8"
          itemScope itemType="https://schema.org/LocalBusiness">

          {/* SEO: Sakriveni podaci koji pomažu rankingu */}
          <meta itemProp="name" content="PC Centar" />
          <meta itemProp="image" content="assets/images/logo.png" />
          <meta itemProp="priceRange" content="$$" />
          
          {/* Kontakt / Upiti */}
          <div className="footer-info-item space-y-6">
            <span className={`text-[10px] uppercase tracking-[0.3em] font-bold block mb-4 italic ${useDark ? 'text-gray-300' : 'text-gray-400'}`}>{t('footer.inquiries', '( Upiti )')}</span>
            <div className="text-lg space-y-1 font-medium">
              <a href="mailto:office@pccentar.rs" className="block hover:line-through decoration-2 transition-all">office@pccentar.rs</a>
              <p className="font-light">+381 11 3168 793</p>
              <p className={`text-[10px] mt-4 tracking-widest uppercase ${useDark ? 'text-gray-300' : 'text-gray-400'}`}>{t('footer.workingHoursShort', 'Pon — Pet: 08-16h')}</p>
            </div>
          </div>
          
          {/* ECO Servis */}
          <div className="footer-info-item space-y-6">
            <span className={`text-[10px] uppercase tracking-[0.3em] font-bold block mb-4 italic ${useDark ? 'text-gray-300' : 'text-gray-400'}`}>{t('footer.ecoServiceLabel', '( ECO Servis )')}</span>
            <div className="text-lg space-y-1 font-medium">
              <p className="font-light">+381 011 4527 038</p>
              <p className={`text-[10px] normal-case leading-tight ${useDark ? 'text-gray-300' : 'text-gray-400'}`}>{t('footer.ecoServiceDesc', 'Stručna podrška i servisna rešenja.')}</p>
            </div>
          </div>

          {/* Direkcija */}
          <div className="footer-info-item space-y-6">
            <span className={`text-[10px] uppercase tracking-[0.3em] font-bold block mb-4 italic ${useDark ? 'text-gray-300' : 'text-gray-400'}`}>{t('footer.directionLabel', '( Direkcija )')}</span>
            <div className="text-lg space-y-1 font-light">
              <p>Višnjička 49b</p>
              <p>11000 Beograd, Srbija</p>
              <a href="https://www.google.com/maps/search/?api=1&query=Višnjička+49b,+Beograd,+Srbija" target="_blank" rel="noreferrer" className="inline-block mt-4 text-[10px] font-bold uppercase border-b border-black pb-0.5 hover:text-gray-500">{t('footer.viewOnMap', 'Pogledaj na mapi')}</a>
            </div>
          </div>

          {/* Magacin */}
          <div className="footer-info-item space-y-6">
            <span className={`text-[10px] uppercase tracking-[0.3em] font-bold block mb-4 italic ${useDark ? 'text-gray-300' : 'text-gray-400'}`}>{t('footer.warehouseLabel', '( Magacin )')}</span>
            <div className="text-lg space-y-1 font-light">
              <p>Višnjička 130</p>
              <p>11000 Beograd, Srbija</p>
              <a href="https://www.google.com/maps/search/?api=1&query=Višnjicka+130,+Beograd,+Srbija" target="_blank" rel="noreferrer" className="inline-block mt-4 text-[10px] font-bold uppercase border-b border-black pb-0.5 hover:text-gray-500">{t('footer.viewOnMap', 'Pogledaj na mapi')}</a>
            </div>
          </div>

        </div>
      </div>

      {/* DONJI DEO: BRUTALISTIČKI PC CENTAR NASLOV */}
      <div className="relative border-t border-gray-200 pt-6 md:pt-10">

        {/* Final Bar */}
        <div className="flex flex-row justify-between items-center gap-2 md:gap-4 md:items-end">
          <div className="flex flex-col gap-1 flex-shrink-0 min-w-0">
            <div className={`text-[6px] md:text-[9px] uppercase font-bold tracking-[0.1em] md:tracking-[0.2em] ${useDark ? 'text-gray-300' : 'text-gray-400'}`}>
              <a href="/admin">©</a> <span className="inline">{currentYear}</span><br className="md:hidden" /> <span>PC Centar</span>
            </div>
          </div>
          
          <div className={`text-center font-normal flex-1 ${useDark ? 'text-gray-400' : 'text-gray-500'}`} style={{fontSize: 'clamp(8px, 2vw, 11px)'}}>
            <p className="text-[8px] md:text-[11px] leading-snug md:leading-normal font-medium">
              <span className="block md:inline">Društvo za unutrašnju i spoljnu trgovinu i usluge</span>
              <span className="block md:inline md:ml-1">PC Centar d.o.o. Beograd (Palilula)</span>
            </p>
            <p className="text-[8px] md:text-[11px] leading-snug md:leading-normal font-light">MB: 07916965 · PIB: 100297758</p>
          </div>

          <div className={`text-[6px] md:text-[9px] uppercase font-bold tracking-[0.1em] md:tracking-[0.2em] flex-shrink-0 text-right ${useDark ? 'text-gray-300' : 'text-gray-400'}`}>
            <span>Computerland<br className="md:hidden" /> Group</span>
          </div>
        </div>
      </div>

    </footer>
  );
};

export default Footer;